import {
  ComponentType,
  ElementType,
  HTMLAttributes,
  ReactNode,
  UIEvent,
  useState,
} from "react";
import { ArrowLeft } from "lucide-react";
import { PunchupSidebarNavItem } from "@punchup/ui-common/punchup/primitives/navigation/dashboard-sidebar/PunchupSidebarNavItem";
import { cx } from "../../../../utils/cx";
import styles from "./PunchupDashboardSidebar.module.css";

/** A nested destination under a section row. */
export interface PunchupDashboardSidebarSubItem {
  /** Stable identity — the React key, and what `onItemClick` reports. */
  label: string;
  /** The visible text. */
  title: string;
  /** Where the row goes. Resolved by the caller, as on a top-level item. */
  url: string;
  /** Whether this is the page currently being viewed. */
  active: boolean;
  /**
   * Short qualifier chip after the title, on the same terms as a top-level item's.
   * Two sibling rows that differ only by their stage — a beta tool beside the tool
   * it replaces — carry the difference here rather than in their titles.
   */
  badgeLabel?: string;
}

/** A top-level destination, or a section row that opens nested ones. */
export interface PunchupDashboardSidebarItem {
  /** Stable identity — the React key, and what `onItemClick` reports. */
  label: string;
  /** The visible text. */
  title: string;
  /** Where the row goes. Ignored on a row that has `subitems` — see invariant 3. */
  url: string;
  /**
   * Leading glyph, as a COMPONENT (not an element) — usually a `lucide-react` icon,
   * which the row paints with `currentColor`. Deliberately wider than `LucideIcon`
   * so a caller can also supply a brand mark that paints its OWN colour; see
   * invariant 10.
   */
  Icon: ComponentType<{ className?: string }>;
  /** Whether this is the page currently being viewed. */
  active: boolean;
  /**
   * Short qualifier chip after the title (e.g. `"Beta"`), rendered through
   * `PunchupSidebarNavItem`'s `BetaBadge`. Deliberately a STRING and deliberately
   * NOT named `badge`: the legacy menu model's `badge` holds a pre-built
   * `ReactNode`, and a tier row does not accept one (see invariant 4).
   */
  badgeLabel?: string;
  /**
   * Trailing glyph after the title — same component shape as `Icon`. Rendered
   * through `PunchupSidebarNavItem`'s `trailingIcon` slot, so it takes the caret's
   * grey. Ignored on a row with `subitems`, which shows the disclosure caret there.
   */
  TrailingIcon?: ComponentType<{ className?: string }>;
  /** Nested destinations. Presence turns this row into an expandable section. */
  subitems?: PunchupDashboardSidebarSubItem[];
  /**
   * Makes the row a button that calls this in place of navigating; `url` goes
   * unused. For a row that changes the nav itself, e.g. opens a drilldown (invariant 12).
   */
  onSelect?: () => void;
}

/**
 * A drilled-in list: a header with a back button and a title, then a flat list of
 * child rows. It replaces the destinations and hides the footer while it is set
 * (invariant 11).
 */
export interface PunchupDashboardSidebarDrilldown {
  /**
   * Stable identity: what `onItemClick` reports for the back button, and the
   * `parentItem` it reports for a child row.
   */
  label: string;
  /** The header's visible title. */
  title: string;
  /** Called when the back button is pressed. The caller decides where back goes. */
  onBack: () => void;
  /** The child rows, in render order. */
  items: PunchupDashboardSidebarSubItem[];
}

export interface PunchupDashboardSidebarProps extends Omit<
  HTMLAttributes<HTMLElement>,
  "onClick"
> {
  /**
   * The top region, supplied whole. The caller OWNS the card, which is what the
   * account-menu case needs: a dropdown library takes the card as its trigger
   * (`<PopoverTrigger asChild>`), and can only do that if it renders the card
   * itself. Omit it to render the nav with no header.
   */
  header?: ReactNode;
  /** The nav's destinations, in render order. */
  items: PunchupDashboardSidebarItem[];
  /**
   * Rows pinned below the destinations, in render order (internal tools,
   * settings). Same model as `items` and the same renderer, so the footer is a
   * second list rather than a second component, and a surface with nothing to pin
   * simply omits it.
   */
  footerItems?: PunchupDashboardSidebarItem[];
  /**
   * Replaces the destinations with a back button, a title and a flat list of child
   * rows, and hides the footer. The header stays (invariant 11).
   */
  drilldown?: PunchupDashboardSidebarDrilldown;
  /**
   * The element every navigating row renders as. Pass `next/link`'s `Link` so rows
   * route client-side; it defaults to a plain `<a>` (a full page load). The tier
   * depends on no router, which is why this is injected rather than imported.
   */
  linkComponent?: ElementType;
  /**
   * Fired when a destination is activated. The payload matches what the dashboard's
   * analytics call already takes, so a consumer wires it straight through:
   * `onItemClick={(payload) => trackSidebarItemClick(surface, payload)}`. The tier
   * holds no analytics dependency of its own.
   */
  onItemClick?: (payload: { item: string; parentItem?: string }) => void;
}

/** A section is open when it holds the current page — see invariant 2. */
const holdsCurrentPage = (item: PunchupDashboardSidebarItem) =>
  item.active || Boolean(item.subitems?.some((subitem) => subitem.active));

export function PunchupDashboardSidebar({
  header,
  items,
  footerItems,
  drilldown,
  linkComponent: Link = "a",
  onItemClick,
  className = "",
  ...props
}: PunchupDashboardSidebarProps) {
  // ONLY the sections the user has explicitly toggled. Open-ness is otherwise
  // DERIVED from the route on every render (`holdsCurrentPage`), so the nav can't
  // strand a section shut around the page you are looking at — the failure mode of
  // seeding a `useState` from the route once at mount, which is what the legacy
  // sidebar does. A user's explicit toggle then wins for as long as they stay.
  const [toggledSections, setToggledSections] = useState<
    Record<string, boolean>
  >({});
  const isOpen = (item: PunchupDashboardSidebarItem) =>
    toggledSections[item.label] ?? holdsCurrentPage(item);
  const toggleSection = (item: PunchupDashboardSidebarItem) => {
    const open = isOpen(item);
    setToggledSections((previous) => ({ ...previous, [item.label]: !open }));
  };

  // The top rule appears only once rows have gone up behind it — at rest there is
  // nothing above the list to divide it from, and a permanent line there would say
  // otherwise. The footer's rule is unconditional by contrast: the footer is always
  // a separate region, scrolled or not.
  const [scrolled, setScrolled] = useState(false);
  const handleScroll = (event: UIEvent<HTMLUListElement>) => {
    setScrolled(event.currentTarget.scrollTop > 0);
  };

  const renderSubitem = (
    subitem: PunchupDashboardSidebarSubItem,
    parentItem: string
  ): ReactNode => (
    <li key={subitem.label}>
      <PunchupSidebarNavItem
        asChild
        level="child"
        label={subitem.title}
        badge={subitem.badgeLabel}
        selected={subitem.active}
        onClick={() => onItemClick?.({ item: subitem.label, parentItem })}
      >
        <Link href={subitem.url} />
      </PunchupSidebarNavItem>
    </li>
  );

  const renderItem = (item: PunchupDashboardSidebarItem): ReactNode => {
    const { Icon, TrailingIcon } = item;
    // A section row expands rather than navigates, so it stays a <button> and its
    // `url` goes unused — the destinations are its children (invariant 3).
    if (item.subitems?.length) {
      const open = isOpen(item);
      return (
        <li key={item.label} className={styles.section}>
          {/* No `selected` — a section header expands, it never navigates, so it
              is not a place you can be. The current page is the ONE child link
              that carries it (invariant 9). */}
          <PunchupSidebarNavItem
            icon={<Icon />}
            label={item.title}
            badge={item.badgeLabel}
            expanded={open}
            onClick={() => toggleSection(item)}
          />
          {open && (
            <ul className={styles.subitems}>
              {item.subitems.map((subitem) =>
                renderSubitem(subitem, item.label)
              )}
            </ul>
          )}
        </li>
      );
    }
    if (item.onSelect) {
      const { onSelect } = item;
      return (
        <li key={item.label}>
          <PunchupSidebarNavItem
            icon={<Icon />}
            label={item.title}
            badge={item.badgeLabel}
            trailingIcon={TrailingIcon ? <TrailingIcon /> : undefined}
            onClick={() => {
              onItemClick?.({ item: item.label });
              onSelect();
            }}
          />
        </li>
      );
    }
    return (
      <li key={item.label}>
        <PunchupSidebarNavItem
          asChild
          icon={<Icon />}
          label={item.title}
          badge={item.badgeLabel}
          trailingIcon={TrailingIcon ? <TrailingIcon /> : undefined}
          selected={item.active}
          onClick={() => onItemClick?.({ item: item.label })}
        >
          <Link href={item.url} />
        </PunchupSidebarNavItem>
      </li>
    );
  };

  // The header and its children are one block, laid out as an open section.
  const renderDrilldown = ({
    label,
    title,
    onBack,
    items: children,
  }: PunchupDashboardSidebarDrilldown): ReactNode => (
    <li className={styles.section}>
      <div className={styles.drilldownHeader}>
        <button
          type="button"
          aria-label="Back"
          className={styles.backButton}
          onClick={() => {
            onItemClick?.({ item: label });
            onBack();
          }}
        >
          <ArrowLeft aria-hidden />
        </button>
        <span className={styles.drilldownTitle}>{title}</span>
      </div>
      <ul className={styles.subitems}>
        {children.map((subitem) => renderSubitem(subitem, label))}
      </ul>
    </li>
  );

  const hasFooter = !drilldown && Boolean(footerItems?.length);

  return (
    <nav
      aria-label="Dashboard"
      className={cx(styles.nav, !hasFooter && styles.flush, className)}
      // Stable hook for the padding rule below, which a test cannot read off the
      // class (Jest's `\.css$` mapping zeros class strings — the PunchupTag precedent).
      data-footer={hasFooter ? "true" : "false"}
      {...props}
    >
      {header}
      {/* The list is wrapped because its top rule must NOT scroll with it — an
          overlay inside a scroll box travels with the rows. The wrapper stays put
          and carries the rule; the <ul> inside it is what scrolls. */}
      <div className={styles.itemsRegion} data-scrolled={String(scrolled)}>
        <ul className={styles.items} onScroll={handleScroll}>
          {drilldown ? renderDrilldown(drilldown) : items.map(renderItem)}
        </ul>
      </div>
      {hasFooter ? (
        <ul className={styles.footer}>{footerItems?.map(renderItem)}</ul>
      ) : null}
    </nav>
  );
}
